<template>
  <LoadOnVisible v-on:intersected="loadChartData">
    <b-card no-body>
      <b-card-header class="pt-0 pb-0">
        <div class="w-100 pb-2">
          <b-row class="align-items-center">
            <div class="col">
              <b-card-title class="mb-1 pt-3" title-tag="h5">
                {{ chartTitle }}
              </b-card-title>
            </div>

            <div class="col-auto d-flex align-items-center">
              <div v-if="!isLoadingChartData && chartDataLoaded" class="mr-3 pt-3">
                <strong class="text-primary">{{ totalOrders }} orders</strong>
              </div>

              <div v-if="groupBy === 'byservice' || groupBy === 'deliverybycarrier'" class="mr-3 pt-3">
                <b-form-group class="mb-0">
                  <b-form-input
                    v-model="carrierFilter"
                    placeholder="Filter by carrier..."
                    size="sm"
                    @input="filterChartData"
                  ></b-form-input>
                </b-form-group>
              </div>

              <div v-if="groupBy === 'bywarehouse'" class="mr-3 pt-3">
                <b-form-group class="mb-0">
                  <b-form-input
                    v-model="warehouseFilter"
                    placeholder="Filter by warehouse..."
                    size="sm"
                    @input="filterChartData"
                  ></b-form-input>
                </b-form-group>
              </div>

              <div v-if="showWidgetFilters" class="d-flex align-items-center flex-wrap">
                <div class="mr-2">
                  <b-form-group class="pt-1 mb-0">
                    <b-input-group prepend="From">
                      <b-form-datepicker
                        v-model="fromDate"
                        :max="toDate"
                        @input="reloadChart"
                      ></b-form-datepicker>
                    </b-input-group>
                  </b-form-group>
                </div>

                <div>
                  <b-form-group class="pt-1 mb-0">
                    <b-input-group prepend="To">
                      <b-form-datepicker
                        v-model="toDate"
                        :min="fromDate"
                        @input="reloadChart"
                      ></b-form-datepicker>
                    </b-input-group>
                  </b-form-group>
                </div>
              </div>
            </div>
          </b-row>
        </div>
      </b-card-header>

      <b-card-body>
        <div class="text-center" v-if="isLoadingChartData">
          <b-spinner
            variant="primary"
            class="m-5"
            style="height: 4rem; width: 4rem"
            label="Loading..."
          ></b-spinner>
        </div>
        <div v-else-if="hasError" class="text-center py-5">
          <i class="fa fa-exclamation-triangle fa-3x text-danger mb-3"></i>
          <p class="text-danger mb-3">Failed to load chart data. Please try again.</p>
          <b-button variant="primary" @click="reloadChart">
            <i class="fa fa-refresh"></i> Retry
          </b-button>
        </div>
        <vue-apex-charts
          v-else-if="chartDataLoaded && (chartMode === 'line' || groupBy === 'byservice' || groupBy === 'bywarehouse' || groupBy === 'deliverybycarrier')"
          type="line"
          ref="chart"
          height="400"
          :options="chartData.chartOptions"
          :series="chartData.series"
        />
        <vue-apex-charts
          v-else-if="chartDataLoaded && chartMode === 'piechart' && groupBy === 'general'"
          type="pie"
          ref="chart"
          height="400"
          :options="pieChartData.chartOptions"
          :series="pieChartData.series"
        />
        <div v-else class="text-center py-5">
          <p class="text-muted">No data available for the selected period</p>
        </div>
      </b-card-body>
    </b-card>
  </LoadOnVisible>
</template>

<script>
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';
import { $themeColors } from '@/themeConfig';
import VueApexCharts from 'vue-apexcharts';
import Ripple from 'vue-ripple-directive';
import { LoadOnVisible } from '@/views/components/custom';

export default {
  mixins: [myCommonDataMixin],
  props: {
    groupBy: {
      type: String,
      required: false,
      default: 'general'
    },
    brandId: {
      required: false,
      default: ''
    },
    warehouseId: {
      required: false,
      default: ''
    },
    storefrontId: {
      required: false,
      default: ''
    },
    defaultChartMode: {
      type: String,
      required: false,
      default: 'line'
    },
    showWidgetFilters: {
      type: Boolean,
      required: false,
      default: true
    },
    filterFromDate: {
      type: String,
      required: false,
      default: null
    },
    filterToDate: {
      type: String,
      required: false,
      default: null
    }
  },
  directives: {
    Ripple
  },
  components: {
    VueApexCharts,
    LoadOnVisible
  },
  data() {
    var todaysDate = new Date();
    var lastQuarterFromDate = new Date();
    lastQuarterFromDate.setMonth(todaysDate.getMonth() - 3);

    return {
      isLoadingChartData: true,
      chartDataLoaded: false,
      hasError: false,
      retryCount: 0,
      maxRetries: 2,
      isInitialLoad: true,
      chartMode: this.defaultChartMode,
      chartModeOptions: [
        { text: 'Line', value: 'line' },
        { text: 'Pie', value: 'piechart' }
      ],
      fromDate: this.filterFromDate || lastQuarterFromDate.toISOString().split('T')[0],
      toDate: this.filterToDate || todaysDate.toISOString().split('T')[0],
      totalOrders: 0,
      carrierFilter: '',
      warehouseFilter: '',
      originalSeriesData: [],
      chartData: {
        series: [],
        chartOptions: {
          chart: {
            zoom: {
              enabled: true
            },
            toolbar: {
              show: true,
              tools: {
                download: true,
                selection: true,
                zoom: true,
                zoomin: true,
                zoomout: true,
                pan: true,
                reset: true
              },
              export: {
                csv: {
                  filename: 'order-fulfillment-stats',
                  headerCategory: 'Date',
                  headerValue: 'Value'
                },
                svg: {
                  filename: 'order-fulfillment-stats'
                },
                png: {
                  filename: 'order-fulfillment-stats'
                }
              }
            },
            animations: {
              enabled: false
            }
          },
          markers: {
            size: 3,
            strokeWidth: 2,
            strokeOpacity: 1,
            strokeColors: [$themeColors.light]
          },
          colors: [],
          dataLabels: {
            enabled: false
          },
          stroke: {
            curve: 'smooth',
            width: 2
          },
          grid: {
            xaxis: {
              lines: {
                show: true
              }
            }
          },
          legend: {
            show: true,
            position: 'right'
          },
          tooltip: {
            shared: true,
            intersect: false,
            x: {
              show: true,
              format: 'dd MMM yyyy'
            },
            y: {
              formatter: function(value) {
                return value + ' hours';
              },
              title: {
                formatter: function(seriesName) {
                  return seriesName;
                }
              }
            }
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              rotate: -45,
              rotateAlways: false,
              hideOverlappingLabels: true,
              showDuplicates: false,
              trim: false,
              datetimeFormatter: {
                year: 'yyyy',
                month: "MMM 'yy",
                day: 'dd MMM',
                hour: 'HH:mm'
              }
            }
          },
          yaxis: {
            title: {
              text: 'Hours'
            }
          }
        }
      },
      pieChartData: {
        series: [],
        chartOptions: {
          chart: {
            toolbar: {
              show: false
            }
          },
          labels: [],
          colors: [],
          legend: {
            show: true,
            position: 'right'
          },
          dataLabels: {
            enabled: true
          },
          tooltip: {
            y: {
              formatter: function(value) {
                return value + ' hours';
              }
            }
          }
        }
      }
    };
  },
  created() {
  },
  computed: {
    chartTitle() {
      var titles = {
        'general': 'Order Fulfillment Times - by Hours',
        'byservice': 'Fulfillment Completed Time - by Carrier & Service',
        'bywarehouse': 'Fulfillment Completed Time - by Warehouse',
        'deliverybycarrier': 'Delivery Time - by Carrier'
      };
      return titles[this.groupBy] || 'Order Fulfillment Times';
    }
  },
  methods: {
    reload() {
      // Called by the parent after filters are applied
      if (this.filterFromDate) {
        this.fromDate = this.filterFromDate;
      }
      if (this.filterToDate) {
        this.toDate = this.filterToDate;
      }
      this.retryCount = 0;
      this.isInitialLoad = false;
      this.loadChartData();
    },
    generateColorFromString(str) {
      // Generate a consistent hash from string
      var hash = 0;
      for (var i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }

      // Convert hash to HSL color for better distribution
      var h = Math.abs(hash % 360);
      var s = 65 + (Math.abs(hash) % 20); // 65-85% saturation
      var l = 45 + (Math.abs(hash >> 8) % 15); // 45-60% lightness

      return 'hsl(' + h + ', ' + s + '%, ' + l + '%)';
    },
    setDateRange(daysBack) {
      var todaysDate = new Date();
      var pastDate = new Date();
      pastDate.setDate(todaysDate.getDate() - daysBack);

      this.fromDate = pastDate.toISOString().split('T')[0];
      this.toDate = todaysDate.toISOString().split('T')[0];
      this.reloadChart();
    },
    reloadChart() {
      if (!this.isLoadingChartData) {
        this.retryCount = 0;
        this.isInitialLoad = false;
        this.loadChartData();
      }
    },
    filterChartData() {
      var filterText = '';

      // Determine which filter to use
      if (this.groupBy === 'byservice' || this.groupBy === 'deliverybycarrier') {
        filterText = this.carrierFilter;
      } else if (this.groupBy === 'bywarehouse') {
        filterText = this.warehouseFilter;
      }

      if (!filterText || filterText.trim() === '') {
        // No filter, show all data
        this.updateChartWithData(this.originalSeriesData);
        return;
      }

      // Filter series by name
      var filterTextLower = filterText.toLowerCase();
      var filteredSeries = this.originalSeriesData.filter(function(series) {
        return series.name.toLowerCase().includes(filterTextLower);
      });

      this.updateChartWithData(filteredSeries);
    },
    updateChartWithData(seriesData) {
      if (seriesData && seriesData.length > 0) {
        this.chartDataLoaded = true;

        var self = this;
        var colors = seriesData.map(function(series) {
          return self.generateColorFromString(series.name);
        });

        this.chartData.series = seriesData;
        this.chartData.chartOptions = Object.assign({}, this.chartData.chartOptions, {
          colors: colors
        });

        // Prepare pie chart data
        var pieLabels = [];
        var pieSeries = [];
        seriesData.forEach(function(series) {
          pieLabels.push(series.name);
          var total = series.data.reduce(function(sum, dp) {
            return sum + dp.y;
          }, 0);
          pieSeries.push(total);
        });

        this.pieChartData.series = pieSeries;
        this.pieChartData.chartOptions = Object.assign({}, this.pieChartData.chartOptions, {
          labels: pieLabels,
          colors: colors
        });
      } else {
        this.chartDataLoaded = false;
        this.chartData.series = [];
        this.pieChartData.series = [];
      }
    },
    loadChartData() {
      this.isLoadingChartData = true;
      this.chartDataLoaded = false;
      this.hasError = false;
      this.carrierFilter = '';
      this.warehouseFilter = '';

      var requestData = {
        fromDate: this.fromDate,
        toDate: this.toDate,
        groupBy: this.groupBy
      };

      if (this.brandId) {
        requestData.brandId = this.brandId;
      }

      if (this.warehouseId) {
        requestData.warehouseId = this.warehouseId;
      }

      if (this.storefrontId) {
        requestData.storefrontId = this.storefrontId;
      }

      this.$http
        .post('/api/analytics/order-fulfillment-stats', requestData)
        .then(resp => {
          this.isLoadingChartData = false;
          this.retryCount = 0;
          this.isInitialLoad = false;

          if (resp.data) {
            this.totalOrders = resp.data.totalOrders;

            if (resp.data.series && resp.data.series.length > 0) {
              var seriesData = resp.data.series.map(function(series) {
                return {
                  name: series.seriesName,
                  data: series.dataPoints.map(function(dp) {
                    return {
                      x: new Date(dp.date),
                      y: dp.count
                    };
                  })
                };
              });

              // Store original data for filtering
              this.originalSeriesData = seriesData;

              // Update chart with data
              this.updateChartWithData(seriesData);
            } else {
              this.chartDataLoaded = false;
              this.chartData.series = [];
              this.pieChartData.series = [];
              this.originalSeriesData = [];
            }
          } else {
            this.chartDataLoaded = false;
            this.chartData.series = [];
            this.pieChartData.series = [];
            this.originalSeriesData = [];
            this.totalOrders = 0;
          }
        })
        .catch(err => {
          console.error('Error loading Order Fulfillment Stats:', err);

          // Auto-retry on initial load only
          if (this.isInitialLoad && this.retryCount < this.maxRetries) {
            this.retryCount++;
            var self = this;
            setTimeout(function() {
              self.loadChartData();
            }, 3000);
            return;
          }

          this.isLoadingChartData = false;
          this.chartDataLoaded = false;
          this.hasError = true;
          this.isInitialLoad = false;
          this.chartData.series = [];
          this.pieChartData.series = [];
          this.originalSeriesData = [];
          this.totalOrders = 0;
        });
    }
  },
  mounted() {
  }
};
</script>
